import { IconAlertTriangle, IconCircleCheck, IconInbox, IconRefresh } from '@tabler/icons-react'
import type { ReactNode } from 'react'

import { ApiError, fieldMessage } from '../lib/apiClient'
import type { BadgeTone } from '../lib/status'

/**
 * Keadaan antarmuka bersama untuk setiap tampilan data: memuat, kosong,
 * terfilter habis, dan salah. Setiap keadaan menyebut penyebab dan langkah
 * berikutnya, bukan hanya lingkaran berputar atau kalimat "tidak ada data".
 */

export function LoadingState({ what }: { what: string }) {
  return (
    <div className="state" role="status">
      <p className="state__title">
        <IconRefresh size={20} stroke={1.75} aria-hidden="true" />
        Memuat {what}
      </p>
      <p className="state__body">
        Permintaan sedang dikirim ke server admin. Daftar akan muncul di sini setelah
        jawabannya diterima.
      </p>
    </div>
  )
}

export function EmptyState({
  title,
  body,
  action,
}: {
  title: string
  body: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="state">
      <p className="state__title">
        <IconInbox size={20} stroke={1.75} aria-hidden="true" />
        {title}
      </p>
      <p className="state__body">{body}</p>
      {action ? <div className="state__actions">{action}</div> : null}
    </div>
  )
}

/**
 * Keadaan salah: menyebut apa yang gagal, kode HTTP bila ada, request id untuk
 * penelusuran log, dan tombol coba lagi.
 */
export function ErrorState({
  title,
  error,
  onRetry,
  retrying,
  hint,
}: {
  title: string
  error: unknown
  onRetry?: () => void
  retrying?: boolean
  hint?: ReactNode
}) {
  const apiError = error instanceof ApiError ? error : null
  const detail =
    error instanceof Error && error.message
      ? error.message
      : 'Penyebabnya tidak diketahui. Periksa log server admin.'

  return (
    <div className="state" role="alert">
      <p className="state__title state__title--danger">
        <IconAlertTriangle size={20} stroke={1.75} aria-hidden="true" />
        {title}
      </p>
      <p className="state__body">{detail}</p>
      {apiError ? (
        <p className="state__code">
          {apiError.status > 0 ? `Kode HTTP ${apiError.status}` : 'Tidak ada balasan HTTP'}
          {apiError.code ? ` (${apiError.code})` : ''}
          {apiError.requestId ? `, request id ${apiError.requestId}` : ''}
        </p>
      ) : null}
      {hint ? <p className="state__body">{hint}</p> : null}
      {onRetry ? (
        <div className="state__actions">
          <button type="button" className="btn" onClick={onRetry} disabled={retrying}>
            <IconRefresh size={18} stroke={1.75} aria-hidden="true" />
            {retrying ? 'Mencoba lagi...' : 'Coba lagi'}
          </button>
        </div>
      ) : null}
    </div>
  )
}

/** Keadaan terfilter habis: ada filter aktif, tetapi tidak ada baris yang cocok. */
export function FilteredEmptyState({
  what,
  onClear,
}: {
  what: string
  onClear?: () => void
}) {
  return (
    <div className="state">
      <p className="state__title">
        <IconInbox size={20} stroke={1.75} aria-hidden="true" />
        Tidak ada {what} yang cocok dengan filter
      </p>
      <p className="state__body">
        Filter yang aktif menyaring seluruh baris. Longgarkan salah satu filter, atau
        bersihkan semuanya untuk melihat {what} terbaru.
      </p>
      {onClear ? (
        <div className="state__actions">
          <button type="button" className="btn" onClick={onClear}>
            Bersihkan filter
          </button>
        </div>
      ) : null}
    </div>
  )
}

export function Badge({
  tone = 'neutral',
  children,
}: {
  tone?: BadgeTone
  children: ReactNode
}) {
  return <span className={`badge badge--${tone}`}>{children}</span>
}

export function Alert({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'warning' | 'danger' | 'success'
  children: ReactNode
}) {
  const Icon =
    tone === 'danger' || tone === 'warning'
      ? IconAlertTriangle
      : tone === 'success'
        ? IconCircleCheck
        : IconInbox
  return (
    <div className={`alert alert--${tone}`} role={tone === 'danger' ? 'alert' : undefined}>
      <span className="alert__icon">
        <Icon size={18} stroke={1.75} aria-hidden="true" />
      </span>
      <div>{children}</div>
    </div>
  )
}

/**
 * Pesan validasi 422 untuk satu field. Server mengirim daftar `fields`; pesan
 * yang tampil di bawah isian diambil dari daftar itu, bukan dari teks karangan
 * klien.
 */
export function FieldErrorText({ error, field }: { error: unknown; field: string }) {
  const message = fieldMessage(error, field)
  if (!message) {
    return null
  }
  return (
    <p className="field__error" id={`${field}-error`}>
      <IconAlertTriangle size={16} stroke={1.75} aria-hidden="true" />
      {message}
    </p>
  )
}

/** Teks bantuan singkat di bawah judul halaman. */
export function PageHint({ children }: { children: ReactNode }) {
  return <p className="muted">{children}</p>
}
